import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { Campaign, CampaignSource, CrmAssignee, FirestoreStructure } from '@parkupp/core'
import { CampaignService } from '../../../services/campaign.service'
import { environment } from '../../../../environments/environment'
import { ToastrService } from 'ngx-toastr'
import { Firestore, collection, getDocs, query, where } from '@angular/fire/firestore'

@Component({
    selector: 'app-admin-campaign-form',
    templateUrl: './admin-campaign-form.component.html',
    styleUrls: ['./admin-campaign-form.component.scss'],
})
export class AdminCampaignFormComponent implements OnInit {
    campaignForm: FormGroup
    loading = false

    // Enums for dropdowns
    campaignSources = Object.values(CampaignSource)
    crmAssignees: CrmAssignee[] = []

    baseUrl = environment.host || window.location.origin

    constructor(
        private formBuilder: FormBuilder,
        private campaignService: CampaignService,
        private router: Router,
        private route: ActivatedRoute,
        private toastr: ToastrService,
        private firestore: Firestore
    ) {
        this.campaignForm = this.formBuilder.group({
            campaignName: ['', [Validators.required, Validators.maxLength(20), Validators.pattern(/^[a-zA-Z0-9-]+$/)]],
            description: [''],
            source: ['', Validators.required],
            landingPageUrl: [this.baseUrl, Validators.required],
            leadUser: [''],
        })

        // Auto-transform campaignName: replace spaces and underscores with dashes, convert to lowercase
        this.campaignForm.get('campaignName')?.valueChanges.subscribe((value) => {
            if (value && typeof value === 'string') {
                const sanitized = value.replace(/[\s_]/g, '-').toLowerCase()
                if (sanitized !== value) {
                    this.campaignForm.get('campaignName')?.setValue(sanitized, { emitEvent: false })
                }
            }
        })
    }

    ngOnInit(): void {
        this.loadCrmAssignees()
    }

    async loadCrmAssignees(): Promise<void> {
        const snapshot = await getDocs(query(collection(this.firestore, FirestoreStructure.CRM_ASSIGNEES), where('active', '==', true)))
        this.crmAssignees = snapshot.docs.map((doc) => new CrmAssignee(doc.data(), doc.id))
    }

    get generatedUrl(): string {
        const formValue = this.campaignForm.value
        if (!formValue.landingPageUrl || !formValue.source || !formValue.campaignName) {
            return ''
        }

        try {
            const url = new URL(formValue.landingPageUrl.startsWith('http') ? formValue.landingPageUrl : this.baseUrl + formValue.landingPageUrl)
            url.searchParams.set('p_source', formValue.source)
            url.searchParams.set('p_campaign', formValue.campaignName)
            if (formValue.leadUser) {
                url.searchParams.set('p_lead_user', formValue.leadUser)
            }
            return url.toString()
        } catch (e) {
            return ''
        }
    }

    async save(): Promise<void> {
        if (this.campaignForm.invalid) {
            this.toastr.error('Please fill in all required fields correctly')
            return
        }

        this.loading = true
        const formValue = this.campaignForm.value

        // Check campaign name uniqueness
        const isUnique = await this.campaignService.isCampaignNameUnique(formValue.campaignName)
        if (!isUnique) {
            this.toastr.error('Campaign name already exists. Please choose a unique name.')
            this.loading = false
            return
        }

        try {
            // Create new campaign (always active by default)
            const campaign = new Campaign({
                campaignName: formValue.campaignName,
                description: formValue.description || undefined,
                source: formValue.source,
                landingPageUrl: formValue.landingPageUrl,
                leadUser: formValue.leadUser || undefined,
                isActive: true,
            })
            await this.campaignService.create(campaign)
            this.toastr.success('Campaign created successfully')

            this.router.navigate(['/admin/campaigns'])
        } catch (error) {
            console.error('Error saving campaign:', error)
            this.toastr.error('Failed to save campaign')
            this.loading = false
        }
    }

    cancel(): void {
        this.router.navigate(['/admin/campaigns'])
    }

    copyGeneratedUrl(): void {
        const url = this.generatedUrl
        if (!url) return

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(
                () => {
                    this.toastr.success('URL copied to clipboard!')
                },
                (err) => {
                    console.error('Failed to copy URL:', err)
                    this.fallbackCopy(url)
                }
            )
        } else {
            // Fallback for browsers that don't support clipboard API
            this.fallbackCopy(url)
        }
    }

    private fallbackCopy(text: string): void {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
            const successful = document.execCommand('copy')
            if (successful) {
                this.toastr.success('URL copied to clipboard!')
            } else {
                this.toastr.error('Failed to copy URL')
            }
        } catch (err) {
            console.error('Fallback copy failed:', err)
            this.toastr.error('Failed to copy URL')
        }

        document.body.removeChild(textArea)
    }
}
